import { useQuery } from "@tanstack/react-query";

import { fetchDataGet } from "@/core/api/api-methods";
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  normalizePaged,
  type ListQueryParams,
  type Paged,
} from "@/core/api/pagination";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type { SavingsLockEventDto } from "@/features/savings/api/dto";

/**
 * Historique paginé des blocages d'un coffre
 * (`GET /SavingsVaults/{id}/lock-events?page=&pageSize=`, du plus récent au
 * plus ancien : LOCKED / EXTENDED / EARLY_UNLOCKED) — réponse `Paged` ;
 * `normalizePaged` tolère un tableau brut transitoire.
 */
export const useSavingsVaultLockEvents = (
  vaultId: string,
  params: ListQueryParams = {},
  enabled = true
) => {
  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  return useQuery({
    queryKey: QUERY_KEYS.SAVINGS_VAULT_LOCK_EVENTS(vaultId, {
      ...params,
      page,
      pageSize,
    }),
    queryFn: async () => {
      const data = await fetchDataGet<
        Paged<SavingsLockEventDto> | SavingsLockEventDto[] | SavingsLockEventDto
      >({
        url: savingsEndpoints.savingsVaults.lockEvents(vaultId),
        queryParams: {
          ...params.filters,
          page: String(page),
          pageSize: String(pageSize),
        },
      });
      return normalizePaged(data, pageSize);
    },
    retry: (failureCount, error) =>
      !/401/.test(error?.message ?? "") && failureCount < 1,
    enabled: enabled && Boolean(vaultId),
  });
};
